import classNames from 'classnames';
import React from 'react';

import { Responsive } from '../helpers/responsive';
import { responsiveDimensions } from '../helpers/responsiveDimensions';

// Typed props map to the `.responsive-*` utilities via the `responsiveDimensions`
// helper; `className` / `style` / `...rest` forward to the underlying element
// (`...rest` also forwards HTML attributes and `ref`).
/* eslint-disable react/require-default-props, react/jsx-props-no-spreading */

type RCResponsiveBoxProps = React.ComponentPropsWithoutRef<'div'> & {
  /** Arbitrary, off-scale width — a CSS length, or a `{ desktop, tablet, mobile }` object to vary by breakpoint. */
  width?: Responsive<string>;
  /** Arbitrary, off-scale height — a CSS length, or a `{ desktop, tablet, mobile }` object to vary by breakpoint. */
  height?: Responsive<string>;
  /** Arbitrary, off-scale min-width — a CSS length, or a `{ desktop, tablet, mobile }` object to vary by breakpoint. */
  minWidth?: Responsive<string>;
  /** Arbitrary, off-scale max-width — a CSS length, or a `{ desktop, tablet, mobile }` object to vary by breakpoint. */
  maxWidth?: Responsive<string>;
  /** Arbitrary, off-scale min-height — a CSS length, or a `{ desktop, tablet, mobile }` object to vary by breakpoint. */
  minHeight?: Responsive<string>;
  /** Arbitrary, off-scale max-height — a CSS length, or a `{ desktop, tablet, mobile }` object to vary by breakpoint. */
  maxHeight?: Responsive<string>;
};

/**
 * Utility component — an unopinionated box for **off-scale, per-breakpoint** width /
 * height, backed by the `.responsive-*` utilities (not a `useMediaQuery` hook, so it's
 * SSR-correct: no null-until-mount, no layout shift).
 *
 * This is an *escape hatch* for hyper-specific dimensions that aren't on any scale — e.g.
 * a hero image that's `340px` tall on desktop and `180px` on mobile. Reach here only for
 * genuine one-off values.
 *
 * **Relationship to custom CSS / when to use which:**
 * - On-scale value (a `%`, `rem`, or `size-*` token fits)? Use the enumerated width /
 *   height utility classes (`.width-50`, `.width-size-16`, `.tablet-width-*`). Prefer these.
 * - Off-scale value, but the component **already has its own stylesheet**? Add it there with a
 *   breakpoint mixin as needed. — `@include tablet { … }` / `@include mobile { … }` — Don't
 *   reach for this component when you're already writing CSS.
 * - Off-scale value in a component that is **otherwise composed entirely of components /
 *   utilities** (no custom CSS)? This is the case this component exists for: it keeps you from
 *   spinning up a whole `.scss` file + class for a single declaration. That's its only real
 *   advantage over hand-written CSS — the mechanics (static class + inline CSS var) are the
 *   same, it just spares you the extra file/import.
 *
 * For layout, prefer RCFlex.
 *
 * Because it brings its own DOM node, it's also the clean way to size something that
 * doesn't forward `className` (e.g. wrapping an RCImage) — no need to fight the child's
 * internals:
 *
 * ```tsx
 * <RCResponsiveBox height={{ desktop: '340px', mobile: '180px' }}>
 *   <RCImage src={…} imageBox="fullWidth" />
 * </RCResponsiveBox>
 * ```
 *
 * To decorate an element you *already* render (rather than introduce a wrapper), skip the
 * component and spread the `responsiveDimensions()` helper onto it directly.
 */
export const RCResponsiveBox = React.forwardRef<HTMLDivElement, RCResponsiveBoxProps>(
  ({ width, height, minWidth, maxWidth, minHeight, maxHeight, className, style, children, ...rest }, ref) => {
    const dimensions = responsiveDimensions({ width, height, minWidth, maxWidth, minHeight, maxHeight });

    return (
      <div
        ref={ref}
        className={classNames(dimensions.className, className) || undefined}
        style={{ ...dimensions.style, ...style }}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

RCResponsiveBox.displayName = 'RCResponsiveBox';
